import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { isAfter, isBefore } from 'date-fns';
import { ScheduleUtil } from './schedule.util';
import { Nationality } from '@shared/enums';
import { BELGIAN_REGISTER_NUMBER_PATTERN } from '@shared/consts';

export class FormValidators extends Validators {
  /**
   * Creates a validator function to check if a date is a valid start date for a date range.
   *
   * @param endDate - The end date of the range to compare against.
   * @returns A validator function that returns ValidationErrors if the date is invalid, or null if valid.
   */
  public static validDateRangeStart(endDate: Date): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const { value } = control;

      if (!(value instanceof Date)) return null;

      if (isAfter(value, endDate)) return { unvalidDateRangeStart: true };

      return null;
    };
  }

  /**
   * Creates a validator function to check if a date is a valid end date for a date range.
   *
   * @param startDate - The start date of the range to compare against.
   * @returns A validator function that returns ValidationErrors if the date is invalid, or null if valid.
   */
  public static validDateRangeEnd(startDate: Date): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const { value } = control;

      if (!(value instanceof Date)) return null;

      if (isBefore(value, startDate)) return { unvalidDateRangeEnd: true };

      return null;
    };
  }

  /**
   * Creates a validator function to check if an array control has at least one element.
   *
   * @usageNotes
   * This validator is typically applied to form controls that should contain non-empty arrays.
   * It checks if the array has at least one element by verifying that the length property is greater than zero.
   *
   * @returns A validator function that returns ValidationErrors with a 'nonEmptyArray' error
   * if the control value is an empty array, or null if the array contains at least one element.
   */
  public static nonEmptyArray(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const { value } = control;

      if (!value.length) {
        console.warn(`'Empty array for the control value : ${value}`);
        return { nonEmptyArray: true };
      }

      return null;
    };
  }

  /**
   * Creates a validator function to check if at least one control in a form group has a value.
   *
   * @usageNotes
   * This validator is typically applied to a FormGroup to ensure that at least one field
   * within the group has a value. It's useful for optional form sections where at least
   * one field must be filled out.
   *
   * @returns A validator function that returns ValidationErrors with an 'atLeastOneValue' error
   * if no control in the form group has a value, or null if at least one control has a value.
   */
  public static atLeastOneValue(): ValidatorFn {
    return (form: AbstractControl) => {
      const { controls } = form as FormGroup | FormArray;

      const hasAValue = Object.values(controls).some((control) => !!control.value);

      if (hasAValue) return null;

      return { atLeastOneValue: true };
    };
  }

  /**
   * Creates a validator function to check if a date is in the future.
   * @usageNotes
   * This validator is typically applied to form controls that should contain future dates, such as deadlines or event dates.
   * It checks if the date value is in the past using the ScheduleUtil.isInThePast() method.
   * @param acceptTodayAsFuture - A boolean parameter that determines whether the current date should be considered valid (true) or invalid (false) when validating future dates.
   * @returns A validator function that returns ValidationErrors with a 'dateInThePast' error if the date is in the past, or null if the date is in the future or not a valid date.
   */
  public static inFuture(acceptTodayAsFuture: boolean): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const { value } = control;

      if (!(value instanceof Date)) return null;

      const isToday =
        new Date().getFullYear() === value.getFullYear() &&
        new Date().getMonth() === value.getMonth() &&
        new Date().getDate() === value.getDate();

      if (isToday) return null;

      if (ScheduleUtil.isInThePast(value)) return { dateInThePast: true };

      return null;
    };
  }

  /**
   * Creates a validator function to check if a date is in the past.
   * @usageNotes
   * This validator is typically applied to form controls that should contain past dates, such as birthdates or historical event dates.
   * It checks if the date value is in the future using the ScheduleUtil.isInTheFuture() method.
   * @param acceptTodayAsPast - A boolean parameter that determines whether the current date should be considered valid (true) or invalid (false) when validating past dates.
   * @returns A validator function that returns ValidationErrors with a 'dateInTheFuture' error if the date is in the future, or null if the date is in the past or not a valid date.
   *
   */
  public static inThePast(acceptTodayAsPast: boolean): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const { value } = control;

      if (!(value instanceof Date)) return null;

      const isToday =
        new Date().getFullYear() === value.getFullYear() &&
        new Date().getMonth() === value.getMonth() &&
        new Date().getDate() === value.getDate();

      if (isToday) return null;

      if (ScheduleUtil.isInTheFuture(value)) return { dateInTheFuture: true };

      return null;
    };
  }

  /**
   * Creates a validator function to check if at least one control in a form group has a non-null value.
   * @usageNotes * This validator is typically applied to a FormGroup to ensure that at least one field
   * within the group has a non-null value. It's useful for optional form sections where at least
   * one field must be filled out with a non-null value.
   * @returns  A validator function that returns ValidationErrors with an 'atLeastOneNotNull' error
   * if no control in the form group has a non-null value, or null if at least one control has a non-null value.
   */
  public static atLeastOneNotNull(): ValidatorFn {
    return (form: AbstractControl) => {
      const { controls } = form as FormGroup;

      const hasANotNullValue = Object.values(controls).some((control) =>
        Array.isArray(control.value) ? control.value.length > 0 : control.value !== null
      );

      if (!hasANotNullValue)
        return {
          atLeastOneNotNull: true,
        };

      return null;
    };
  }

  /**
   * Creates a validator function to check if a string is not empty.
   * @usageNotes
   * This validator is typically applied to form controls that should contain non-empty strings, such as names or descriptions.
   * It checks if the string value is empty or consists only of whitespace characters by trimming the value and checking its length.
   * @returns A validator function that returns ValidationErrors with a 'notEmptyString' error if the string is empty or whitespace, or null if the string contains non-whitespace characters.
   */
  public static notEmptyString(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const { value } = control;

      if (typeof value === 'string' && value.trim() === '') {
        return { notEmptyString: true };
      }

      return null;
    };
  }

  public static positive(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const { value } = control;

      if (typeof value === 'number' && value < 0) {
        return { positive: true };
      }

      return null;
    };
  }

  /**
   * Creates a validator function to check if a string is a valid Belgian register number when the nationality is Belgian.
   * @usageNotes
   * This validator is typically applied to form controls that should contain a Belgian register number when
   * @returns A validator function that returns ValidationErrors with a 'registerNumber' error if the string is not a valid Belgian register number when the nationality is Belgian, or null if the string is valid or the nationality is not Belgian.
   */
  public static registerNumber(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const { value } = control;

      if (typeof value !== 'string' || value !== Nationality.BE) return null;

      const isValidPattern = BELGIAN_REGISTER_NUMBER_PATTERN.test(value);

      if (!isValidPattern) {
        return { registerNumber: true };
      }

      return null;
    };
  }

  /**
   * Creates a validator function to check that a FormArray does not exceed a maximum number of items.
   *
   * @param max - The maximum number of items allowed in the FormArray.
   * @returns A validator function that returns ValidationErrors with a 'maxItems' error
   * if the FormArray length exceeds the maximum, or null if the control is not a FormArray or the length is within bounds.
   */
  public static maxItems(max: number): ValidatorFn {
    return (formArray: AbstractControl): ValidationErrors | null => {
      if (!(formArray instanceof FormArray)) return null;

      return formArray.length > max ? { maxItems: true } : null;
    };
  }
}
